import { Button, Select, Typography } from "@/deps/ui-library";
import type { TablePaging } from "./DataTable";
import { PAGE_SIZES, type ListSearch } from "./listQuery";

export interface ListFooterProps {
  search: ListSearch;
  onChange: (next: ListSearch) => void;
  /** How many rows are on this page. */
  shown: number;
  /** How many match the filters in total. */
  total: number;
  hasNext: boolean;
}

/**
 * The URL's list state as the paging under an RDS `DataTable` — what `ListScreen` gives its table.
 *
 * Changing the page size returns to the first page: row 26 of 25-per-page and row 26 of
 * 100-per-page are different rows, so staying on "page 2" would silently move you.
 */
export function listPaging(
  search: ListSearch,
  onChange: (next: ListSearch) => void,
  total: number,
): TablePaging {
  return {
    page: search.page ?? 1,
    size: search.size ?? 25,
    total,
    sizes: PAGE_SIZES,
    onPageChange: (page) => onChange({ ...search, page: page <= 1 ? undefined : page }),
    onSizeChange: (size) =>
      onChange({ ...search, size: size as ListSearch["size"], page: undefined }),
  };
}

/**
 * A footer for a list whose table does not page itself: what you are looking at, how many per
 * page, and the way forward. `ListScreen` no longer uses it — its RDS table carries the same paging
 * (`listPaging`) — but a screen with a hand-laid list still can.
 */
export function ListFooter({ search, onChange, shown, total, hasNext }: ListFooterProps) {
  const page = search.page ?? 1;
  const size = search.size ?? 25;
  const first = total === 0 ? 0 : (page - 1) * size + 1;
  const last = (page - 1) * size + shown;

  return (
    <div className="border-border flex flex-wrap items-center justify-between gap-3 border-t px-3 py-2">
      {/* Concrete numbers, per the RDS voice — "Showing 26–50 of 142", not "Page 2". Somebody
          reconciling a figure needs to know how much of it they are looking at. */}
      <Typography.Text as="span" variant="small" muted>
        {total === 0 ? (
          "No matching documents"
        ) : (
          <>
            Showing <span className="num">{first}</span>–<span className="num">{last}</span> of{" "}
            <span className="num">{total}</span>
          </>
        )}
      </Typography.Text>

      <div className="flex items-center gap-3">
        <div className="flex w-40 items-center gap-1.5 [&_label]:sr-only">
          <Typography.Text as="span" variant="small" muted>
            Rows
          </Typography.Text>
          <Select
            label="Rows per page"
            value={String(size)}
            options={PAGE_SIZES.map((option) => ({ value: String(option), label: String(option) }))}
            showClearButton={false}
            onChange={(details) => {
              const chosen = Array.isArray(details.value) ? details.value[0] : details.value;
              if (chosen) listPaging(search, onChange, total).onSizeChange(Number(chosen.value));
            }}
          />
        </div>

        <div className="flex items-center gap-1">
          <Button
            colorScheme="default"
            variant="ghost"
            size="sm"
            disabled={page <= 1}
            onClick={() => onChange({ ...search, page: page <= 2 ? undefined : page - 1 })}
          >
            Previous
          </Button>
          <Button
            colorScheme="default"
            variant="ghost"
            size="sm"
            disabled={!hasNext}
            onClick={() => onChange({ ...search, page: page + 1 })}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
